import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
} from 'react';
import classNames from 'classnames';
import { replaceTemplateWithElements, useLanguageStore } from '$/store/languageStore';
import IconFont from '$/components/IconFont';
import { message } from '$/components/Message';
import styles from './index.module.scss';

interface ImageUploaderProps {
  onFilesSelected: (files: File[]) => void;
  /** 打开文件选择框前的可选拦截函数：返回 false 会取消本次打开；未传时保持原上传行为。 */
  beforeOpen?: () => boolean;
  /** 上传入口被触发时调用，用于点击类埋点前置上报。 */
  onOpen?: () => void;
  maxCount?: number;
  currentCount?: number;
  disabled?: boolean;
  accept?: string;
  className?: string;
  title?: string;
  description?: string;
  buttonText?: string;
  children?: React.ReactNode;
  openKey?: number;
}

/** AI 工具共用的文件选择与拖放入口，保留上传前置校验和数量限制。 */
export default function ImageUploader({
  onFilesSelected,
  beforeOpen,
  onOpen,
  maxCount = 30,
  currentCount = 0,
  disabled = false,
  accept = 'image/jpeg,image/png,image/jpg,image/webp',
  className,
  title = '拖拽图片到此处或点击上传',
  description,
  buttonText,
  children,
  openKey,
}: ImageUploaderProps) {
  const [isDragging, setIsDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  // 剩余可上传数量：总上限 maxCount 减去当前已有 currentCount，由组件内部统一限制
  const remainingCount = Math.max(maxCount - currentCount, 0);
  const isUploadDisabled = disabled || remainingCount <= 0;
  const previousOpenKeyRef = useRef<number | undefined>(openKey);
  const languageStore = useLanguageStore((state) => state.languageStore);
  const resolvedDescription = description ?? languageStore.smartcatTranslationText((v) => v.supported_formats_background);
  const resolvedButtonText = buttonText ?? languageStore.smartcatTranslationText((v) => v.ai_enhance_upload);

  // 拖拽上传也复用同一前置判断，避免绕过登录与禁用态。
  const canOpenUpload = useCallback(() => {
    if (isUploadDisabled) return false;
    return beforeOpen?.() !== false;
  }, [beforeOpen, isUploadDisabled]);

  // 用户触发上传入口时先上报点击，再交给 beforeOpen 决定是否真正打开选择框。
  const openUpload = useCallback(() => {
    if (isUploadDisabled) return;
    onOpen?.();
    if (beforeOpen?.() !== false) {
      inputRef.current?.click();
    }
  }, [beforeOpen, isUploadDisabled, onOpen]);

  useEffect(() => {
    if (openKey === undefined) return;
    if (previousOpenKeyRef.current === openKey) return;
    previousOpenKeyRef.current = openKey;
    openUpload();
  }, [openKey, openUpload]);

  const handleFiles = useCallback(
    (files: FileList | File[]) => {
      const rawList = Array.from(files);

      const acceptedTypes = accept.split(',').map((t) => t.trim().toLowerCase());
      const formatErrorFiles = rawList.filter((file) => {
        return !acceptedTypes.some((type) => {
          if (type.startsWith('.')) {
            return file.name.toLowerCase().endsWith(type);
          }
          if (type.endsWith('/*')) {
            return file.type.startsWith(type.replace('/*', ''));
          }
          if (type === 'image/jpg') {
            return file.name.toLowerCase().endsWith('.jpg') || file.type === 'image/jpeg';
          }
          return file.type === type;
        });
      });

      const validFiles = rawList.filter((file) => !formatErrorFiles.includes(file));
      const fileArray = validFiles.slice(0, remainingCount);

      const messages: string[] = [];

      if (formatErrorFiles.length > 0) {
        messages.push(replaceTemplateWithElements('roboneo_file_toast1' as any, {}));
      }

      if (validFiles.length > remainingCount) {
        const t1 = replaceTemplateWithElements('roboneo_web_upload_limit_4' as any, {
          file_num: `${maxCount}`,
        });
        const t2 = replaceTemplateWithElements('RoboNeo_Web_file_upload_fail' as any, {
          num: `${validFiles.length - remainingCount}`,
        });
        messages.push(`${t1},${t2}`);
      }

      if (messages.length > 0) {
        message.info(messages.join(' '));
      }

      if (fileArray.length > 0) {
        onFilesSelected(fileArray);
      }
    },
    [remainingCount, maxCount, accept, onFilesSelected],
  );

  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!isUploadDisabled) setIsDragging(true);
    },
    [isUploadDisabled],
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!isUploadDisabled) setIsDragging(true);
    },
    [isUploadDisabled],
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      if (!canOpenUpload()) return;
      const { files } = e.dataTransfer;
      if (files.length > 0) {
        handleFiles(files);
      }
    },
    [handleFiles, canOpenUpload],
  );

  const handleClick = useCallback(() => {
    openUpload();
  }, [openUpload]);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { files } = e.target;
      if (files && files.length > 0) {
        handleFiles(files);
      }
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    },
    [handleFiles],
  );

  // 程序化触发 input.click() 会冒泡到默认上传容器，阻止重复触发入口点击埋点。
  const handleInputClick = useCallback((e: React.MouseEvent<HTMLInputElement>) => {
    e.stopPropagation();
  }, []);

  const inputNode = (
    <input
      ref={inputRef}
      type="file"
      accept={accept}
      multiple={remainingCount > 1}
      className={styles.hidden_input}
      onChange={handleInputChange}
      onClick={handleInputClick}
      disabled={isUploadDisabled}
    />
  );

  if (children && React.isValidElement(children)) {
    const child = children as React.ReactElement<any>;

    return (
      <>
        {React.cloneElement(child, {
          ...child.props,
          role: child.props.role ?? 'button',
          tabIndex: child.props.tabIndex ?? 0,
          'aria-disabled': isUploadDisabled,
          className: classNames(child.props.className, className),
          onClick: (e: React.MouseEvent) => {
            child.props.onClick?.(e);
            handleClick();
          },
          onDragEnter: (e: React.DragEvent) => {
            child.props.onDragEnter?.(e);
            handleDragEnter(e);
          },
          onDragOver: (e: React.DragEvent) => {
            child.props.onDragOver?.(e);
            handleDragOver(e);
          },
          onDragLeave: (e: React.DragEvent) => {
            child.props.onDragLeave?.(e);
            handleDragLeave(e);
          },
          onDrop: (e: React.DragEvent) => {
            child.props.onDrop?.(e);
            handleDrop(e);
          },
          onKeyDown: (e: React.KeyboardEvent) => {
            child.props.onKeyDown?.(e);
            if (e.key === 'Enter' || e.key === ' ') {
              handleClick();
            }
          },
        })}
        {inputNode}
      </>
    );
  }

  return (
    <div className={classNames(styles.upload_outer, className)}>
      <div
        role="button"
        tabIndex={0}
        aria-disabled={isUploadDisabled}
        onClick={handleClick}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleClick();
          }
        }}
        className={classNames(
          styles.upload_inner,
          {
            [styles.dragging]: isDragging,
            [styles.disabled]: isUploadDisabled,
          },
        )}
      >
        <div className={styles.upload_btn}>
          <IconFont name="uploadBold" fontSize={17} className={styles.upload_icon} />
          <span className={styles.upload_btn_text}>{resolvedButtonText}</span>
        </div>

        <div className={styles.text_group}>
          <div className={styles.title}>{title}</div>
          <div className={styles.description}>{resolvedDescription}</div>
        </div>

        {inputNode}
      </div>
    </div>
  );
}
